import React, { useState } from 'react'
import { ChevronRightIcon, ChevronDownIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import Button from 'aldoo-ra/Button'
import { showNotification } from 'aldoo-ra/Notification'

/**
 * StructuredDataViewer - Interactive JSON object visualization with collapsible trees
 * 
 * Features:
 * - Collapsible/expandable object and array nodes
 * - Syntax highlighting for different data types
 * - Copy individual values or entire objects
 * - Compact display for simple values
 * - Deep nesting support with proper indentation
 */
const StructuredDataViewer = ({ 
  data, 
  maxDepth = 10, 
  initialExpandedDepth = 2,
  compact = false,
  className = '',
  onCopy = null
}) => {
  const [expandedPaths, setExpandedPaths] = useState(new Set())
  const [copiedItems, setCopiedItems] = useState(new Set())

  // Initialize expanded paths based on initialExpandedDepth
  React.useEffect(() => {
    if (data && typeof data === 'object' && initialExpandedDepth > 0) {
      const initialPaths = new Set()
      
      const addPathsToDepth = (obj, currentPath = '', depth = 0) => {
        if (depth >= initialExpandedDepth || depth >= maxDepth) return
        
        if (obj && typeof obj === 'object') {
          initialPaths.add(currentPath)
          
          Object.keys(obj).forEach(key => {
            const newPath = currentPath ? `${currentPath}.${key}` : key
            if (obj[key] && typeof obj[key] === 'object') {
              addPathsToDepth(obj[key], newPath, depth + 1)
            }
          })
        }
      }
      
      addPathsToDepth(data)
      setExpandedPaths(initialPaths)
    }
  }, [data, initialExpandedDepth, maxDepth])

  const toggleExpanded = (path) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  const copyToClipboard = async (value, itemId) => {
    try {
      const textToCopy = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
      await navigator.clipboard.writeText(textToCopy)
      
      setCopiedItems(prev => new Set(prev).add(itemId))
      
      if (onCopy) {
        onCopy(textToCopy, itemId)
      } else {
        showNotification({
          type: 'success',
          title: 'Copied!',
          message: 'Value copied to clipboard',
          duration: 2000
        })
      }
      
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev)
          newSet.delete(itemId)
          return newSet
        })
      }, 1500)
    } catch (err) {
      console.error('Failed to copy:', err)
      showNotification({
        type: 'error',
        title: 'Copy Failed',
        message: 'Unable to copy to clipboard',
        duration: 3000
      })
    }
  }

  const getValueTypeColor = (value) => {
    if (value === null) return 'text-gray-500 italic'
    if (value === undefined) return 'text-gray-400 italic'
    if (typeof value === 'string') return 'text-emerald-600'
    if (typeof value === 'number') return 'text-blue-600'
    if (typeof value === 'boolean') return 'text-purple-600'
    if (Array.isArray(value)) return 'text-orange-600'
    if (typeof value === 'object') return 'text-amber-600'
    return 'text-admin_text'
  }

  const formatValue = (value) => {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value === 'string') return `"${value}"`
    if (typeof value === 'boolean') return value.toString()
    if (typeof value === 'number') return value.toString()
    return JSON.stringify(value)
  }

  const renderValue = (value, key, path = '', depth = 0) => {
    const itemId = `${path}-${key}-${depth}`
    const currentPath = path ? `${path}.${key}` : key
    const isArray = Array.isArray(value)
    const isObject = value && typeof value === 'object' && !isArray
    const isPrimitive = !isArray && !isObject
    const isExpanded = expandedPaths.has(currentPath)
    const exceedsMaxDepth = depth >= maxDepth

    if (isPrimitive) {
      return (
        <div className="flex items-center space-x-2 group">
          <span className="text-admin_text_secondary">
            {key}:
          </span>

          <span className={getValueTypeColor(value)}>
            {formatValue(value)}
          </span>

          <Button
            variant="admin_icon"
            onClick={(e) => {
              e.stopPropagation()
              copyToClipboard(value, itemId)
            }}
            className="text-admin_neutral_3 opacity-0 group-hover:opacity-100 transition p-1 h-6 w-6"
            title="Copy value"
          >
            {copiedItems.has(itemId) ? (
              <span className="text-admin_success text-xs">✓</span>
            ) : (
              <DocumentDuplicateIcon className="w-3 h-3" />
            )}
          </Button>
        </div>
      )
    }

    if (exceedsMaxDepth) {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-admin_text_secondary">
            {key}:
          </span>

          <span className="text-admin_text_secondary italic">
            {isArray ? `[${value.length} items] (max depth reached)` : `{...} (max depth reached)`}
          </span>
        </div>
      )
    }

    const objectSize = isArray ? value.length : Object.keys(value).length

    return (
      <div>
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:bg-admin_neutral_1 rounded px-1 py-0.5 group"
          onClick={() => toggleExpanded(currentPath)}
        >
          <div className="flex items-center space-x-1">
            {isExpanded ? (
              <ChevronDownIcon className="w-3 h-3 text-admin_text_secondary" />
            ) : (
              <ChevronRightIcon className="w-3 h-3 text-admin_text_secondary" />
            )}

            <span className="text-admin_text_secondary">
              {key}:
            </span>

            <span className={getValueTypeColor(value)}>
              {isArray ? `Array[${objectSize}]` : `Object{${objectSize}}`}
            </span>
          </div>
          
          <Button
            variant="admin_icon"
            onClick={(e) => {
              e.stopPropagation()
              copyToClipboard(value, itemId)
            }}
            className="h-6 w-6 text-admin_neutral_3 opacity-0 group-hover:opacity-100 transition p-1"
            title="Copy entire object"
          >
            {copiedItems.has(itemId) ? (
              <span className="text-admin_success text-xs">
                ✓
              </span>
            ) : (
              <DocumentDuplicateIcon className="w-3 h-3" />
            )}
          </Button>
        </div>
        
        {isExpanded && (
          <div className="ml-4 border-l border-admin_bordered pl-3 mt-1 space-y-1">
            {isArray ? (
              value.map((item, index) => (
                <div key={index}>
                  {renderValue(item, `[${index}]`, currentPath, depth + 1)}
                </div>
              ))
            ) : (
              Object.entries(value).map(([nestedKey, nestedValue]) => (
                <div key={nestedKey}>
                  {renderValue(nestedValue, nestedKey, currentPath, depth + 1)}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  if (!data || (typeof data !== 'object')) {
    return (
      <div className={`text-admin_text_secondary italic ${className}`}>
        No structured data
      </div>
    )
  }

  const rootObjectSize = Array.isArray(data) ? data.length : Object.keys(data).length

  if (compact && rootObjectSize === 0) {
    return (
      <div className={`text-admin_text_secondary italic ${className}`}>
        Empty object
      </div>
    )
  }

  return (
    <div className={`font-mono text-sm ${className}`}>
      <div className="space-y-1">
        {Array.isArray(data) ? (
          data.map((item, index) => (
            <div key={index}>
              {renderValue(item, `[${index}]`, '', 0)}
            </div>
          ))
        ) : (
          Object.entries(data).map(([key, value]) => (
            <div key={key}>
              {renderValue(value, key, '', 0)}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default StructuredDataViewer